import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { getUrineTestResults, updateLabData } from "../services/labService";
import MainHeader from "../Components/MainHeader";
import CustomAlert from "../Components/CustomAlert";
import { useCustomAlert } from "../hooks/useCustomAlert";
import UrineTestFormula from "../formula/UrineTest";
import {
  URINE_EDITABLE_FIELDS,
  URINE_FIELD_SPECS,
  buildUrineFormulaData,
  getUrineDisplayEntries,
  getUrineUpdateName,
} from "../utils/urineDataUtils";

const TestResultsContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: "Noto Sans Thai", sans-serif !important;
  * {
    font-family: "Noto Sans Thai", sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00bbd3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const StatusCard = styled.div`
  background: ${(props) => props.$bgColor || "#34A853"};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UrineIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const ResultCard = styled.div`
  background-color: white;
  border-radius: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 24px;
`;

const CardHeader = styled.div`
  background-color: #00bbd3;
  padding: 16px 20px;
  color: white;
  text-align: center;
`;

const HeaderText = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  color: white;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const CardContent = styled.div`
  padding: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-size: 16px;
  color: #666666;
  font-family: "Noto Sans Thai", sans-serif !important;
  font-weight: ${(props) => (props.bold ? "bold" : "normal")};
  flex: 1;
`;

const InfoValue = styled.span`
  font-size: 16px;
  color: #666666;
  font-family: "Noto Sans Thai", sans-serif !important;
  font-weight: bold;
  text-align: right;
  flex: 1;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: #e1e5e9;
  margin: 16px 0;
`;

const SectionTitle = styled.h4`
  font-size: 16px;
  font-weight: bold;
  color: #333333;
  margin: 20px 0 12px 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const HistorySection = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const HistoryTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeaderStyled = styled.thead`
  background-color: #00bbd3;
  color: white;
`;

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  font-family: "Noto Sans Thai", sans-serif !important;
  color: white;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  color: #666;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  svg {
    width: 20px;
    height: 20px;
    fill: #34a853;
  }

  &:hover svg {
    fill: #45a049;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: "Noto Sans Thai", sans-serif !important;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00bbd3;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  font-family: "Noto Sans Thai", sans-serif !important;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SaveButton = styled(Button)`
  background-color: #00bbd3;
  color: white;
`;

const CancelButton = styled(Button)`
  background-color: #e0e0e0;
  color: #666;
`;

const parseFlexibleDate = (dateValue) => {
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
  }

  const raw = String(dateValue).trim();
  if (!raw) return null;

  const isoDate = new Date(raw);
  if (!Number.isNaN(isoDate.getTime())) {
    return isoDate;
  }

  const ymdMatch = raw.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/);
  if (ymdMatch) {
    const [, yearText, monthText, dayText] = ymdMatch;
    return new Date(Number(yearText), Number(monthText) - 1, Number(dayText));
  }

  const dmyMatch = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
  if (dmyMatch) {
    const [, dayText, monthText, yearText] = dmyMatch;
    let year = Number(yearText);
    if (yearText.length === 2) {
      year += year >= 50 ? 1900 : 2000;
    }
    return new Date(year, Number(monthText) - 1, Number(dayText));
  }

  return null;
};

const formatDisplayDate = (dateString) => {
  if (!dateString) return "-";
  const date = parseFlexibleDate(dateString);
  if (!date) return dateString;
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear() + 543;
  return `${day}/${month}/${year}`;
};

const colorMap = {
  green: "#34A853",
  red: "#ff4757",
  gray: "#e0e0e0",
};

const UrineTestResultsPage = () => {
  const navigate = useNavigate();
  const { alertState, showAlert, closeAlert } = useCustomAlert();
  const [urineResults, setUrineResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusColor, setStatusColor] = useState("#e0e0e0");
  const [abnormalList, setAbnormalList] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({});

  const loadUrineResults = async () => {
    try {
      setLoading(true);
      const response = await getUrineTestResults();

      if (response?.result && Array.isArray(response.list)) {
        const formattedResults = response.list.map((item) => ({
          date: item.visit_date || item.dateadd || "-",
          hospital: item.hospital_name || "โรงพยาบาล",
          results: getUrineDisplayEntries(item),
          rawData: item,
        }));

        const sortedResults = [...formattedResults].sort(
          (a, b) =>
            (parseFlexibleDate(b.rawData?.dateadd || b.date)?.getTime() || 0) -
            (parseFlexibleDate(a.rawData?.dateadd || a.date)?.getTime() || 0),
        );

        setUrineResults(sortedResults);

        if (sortedResults.length > 0) {
          const latestItem = sortedResults[0].rawData;
          const formulaResult = UrineTestFormula(
            buildUrineFormulaData(latestItem),
          );
          setStatusColor(colorMap[formulaResult.color] || "#e0e0e0");
          setAbnormalList(formulaResult.abnormalFindings || []);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUrineResults();
  }, []);

  const handleEditClick = (record) => {
    setEditingRecord(record);
    const form = {};
    URINE_EDITABLE_FIELDS.forEach((key) => {
      form[key] = record.rawData?.[key] ?? "";
    });
    setEditForm(form);
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      const updates = [];

      URINE_EDITABLE_FIELDS.forEach((key) => {
        const nextValue = editForm[key];
        const currentValue = editingRecord?.rawData?.[key] ?? "";
        if (nextValue !== undefined && nextValue !== currentValue) {
          const dbName = getUrineUpdateName(key);
          updates.push(
            updateLabData(
              editingRecord.rawData.dateadd,
              dbName,
              nextValue,
              "h",
            ),
          );
        }
      });

      if (updates.length > 0) {
        await Promise.all(updates);
      }

      showAlert("อัปเดตข้อมูลเรียบร้อยแล้ว");
      setShowEditModal(false);
      setEditingRecord(null);
      await loadUrineResults();
    } catch (error) {
      showAlert("เกิดข้อผิดพลาดในการอัปเดตข้อมูล");
    }
  };

  return (
    <TestResultsContainer>
      <MainHeader />
      <TopBar>ผลตรวจปัสสาวะ</TopBar>
      <Header>
        <BackButton onClick={() => navigate("/dashboard")}>
          &lt; ย้อนกลับ
        </BackButton>
        <PageTitle>ผลตรวจปัสสาวะ (Urine Test)</PageTitle>
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : urineResults.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
            ไม่พบข้อมูลผลตรวจปัสสาวะ
          </div>
        ) : (
          <>
            <StatusCard $bgColor={statusColor}>
              <UrineIcon src="./ผลการตรวจปัสสาวะสี.png" alt="ผลตรวจปัสสาวะ" />
              <StatusInfo>
                <StatusTitle style={{ color: "white" }}>
                  {abnormalList.length > 0
                    ? `ค่าที่ผิดปกติ: ${abnormalList.join(", ")}`
                    : "ผลตรวจปกติ"}
                </StatusTitle>
              </StatusInfo>
            </StatusCard>

            {urineResults.map((result, index) => (
              <ResultCard key={index}>
                <CardHeader>
                  <HeaderText>ผลตรวจปัสสาวะ</HeaderText>
                </CardHeader>
                <CardContent>
                  <InfoRow>
                    <InfoLabel bold>วันที่ตรวจ</InfoLabel>
                    <InfoValue>{formatDisplayDate(result.date)}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel bold>โรงพยาบาล</InfoLabel>
                    <InfoValue>{result.hospital}</InfoValue>
                  </InfoRow>
                  <Divider />
                  <SectionTitle>รายการตรวจ</SectionTitle>
                  {result.results.map((entry, idx) => (
                    <InfoRow key={idx}>
                      <InfoLabel>{entry.label}</InfoLabel>
                      <InfoValue>{entry.value}</InfoValue>
                    </InfoRow>
                  ))}
                </CardContent>
              </ResultCard>
            ))}

            <HistorySection>
              <HistoryTitle>ประวัติการตรวจทั้งหมด</HistoryTitle>
              <Table>
                <TableHeaderStyled>
                  <tr>
                    <TableHeaderCell>วันที่ตรวจ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่วัด</TableHeaderCell>
                    <TableHeaderCell>ความเสี่ยง</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </tr>
                </TableHeaderStyled>
                <tbody>
                  {urineResults.map((record, index) => {
                    const values = record.results
                      .map((entry) => `${entry.label}: ${entry.value}`)
                      .join(", ");
                    const formulaResult = UrineTestFormula(
                      buildUrineFormulaData(record.rawData),
                    );
                    const abnormals = formulaResult?.abnormalFindings || [];
                    const riskStatus =
                      abnormals.length > 0
                        ? `ผิดปกติ (${abnormals.join(", ")})`
                        : "ปกติ";
                    const riskColor =
                      abnormals.length > 0 ? "#ff4757" : "#34A853";

                    return (
                      <TableRow key={index}>
                        <TableCell>{formatDisplayDate(record.date)}</TableCell>
                        <TableCell
                          style={{ textAlign: "left", fontSize: "12px" }}
                        >
                          {values || "-"}
                        </TableCell>
                        <TableCell
                          style={{ color: riskColor, fontWeight: "bold" }}
                        >
                          {riskStatus}
                        </TableCell>
                        <TableCell>
                          <EditButton onClick={() => handleEditClick(record)}>
                            <svg
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                            </svg>
                          </EditButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </tbody>
              </Table>
            </HistorySection>
          </>
        )}
      </Content>

      {showEditModal && editingRecord && (
        <Modal onClick={() => setShowEditModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>แก้ไขข้อมูล</ModalTitle>
            <FormGroup>
              <Label>วันที่: {formatDisplayDate(editingRecord.date)}</Label>
            </FormGroup>

            {URINE_FIELD_SPECS.map((spec) => (
              <FormGroup key={spec.field}>
                <Label>{spec.label}</Label>
                <Input
                  type="text"
                  value={editForm[spec.field] ?? ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, [spec.field]: e.target.value })
                  }
                  // placeholder="พิมพ์ได้ทั้งตัวเลขและข้อความ"
                />
              </FormGroup>
            ))}

            <ButtonGroup>
              <CancelButton onClick={() => setShowEditModal(false)}>
                ยกเลิก
              </CancelButton>
              <SaveButton onClick={handleEditSubmit}>บันทึก</SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {alertState.isOpen && (
        <CustomAlert message={alertState.message} onClose={closeAlert} />
      )}
    </TestResultsContainer>
  );
};

export default UrineTestResultsPage;
